import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, Settings, FileText, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { FirstLoginDisclaimerModal } from "@/components/Disclaimer";
import { RegulensMark } from "@/components/RegulensMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import AnalyticsNotice from "@/components/AnalyticsNotice";
import { useAnalytics } from "@/lib/analytics";

export const Layout = ({ children, fullWidth = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.is_admin;

  // GA4 SPA page_view tracking. Hook is a no-op when GA is not configured
  // or the merchant has opted out — see lib/analytics.js.
  useAnalytics();

  return (
    <div className="min-h-screen flex flex-col bg-[var(--rg-bg)] text-[var(--rg-text-strong)] grain">
      {user && <FirstLoginDisclaimerModal />}
      <header className="border-b border-[var(--rg-border)] bg-[var(--rg-bg)]/90 backdrop-blur sticky top-0 z-30">
        {/* Header pinned to viewport edges — no max-width centering — so
            the logo always sits at the true top-left corner (x=24) on
            every viewport width. Fixes the "logo appears missing on
            non-homepage pages" perception on ≥1600px displays where the
            earlier `max-w-[1600px] mx-auto` container pushed the mark
            ~160px inward on ultrawide monitors. Content-area max-width
            is still controlled by the `fullWidth` prop on the <main>
            below. */}
        <div className="w-full px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <RegulensMark size={40} theme="dark" className="group-hover:scale-110 transition-fast shrink-0" />
            <span className="font-heading font-extrabold tracking-tight text-xl">REGULENS</span>
            <span className="text-[10px] font-mono text-[var(--rg-muted)] uppercase tracking-[0.2em] ml-1">v4</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/methodology" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] transition-fast" data-testid="nav-methodology">Methodology</Link>
            <Link to="/data-verification" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] transition-fast" data-testid="nav-data-verification">Data Verification</Link>
            <Link to="/sample-report" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] transition-fast" data-testid="nav-sample-report">Sample Report</Link>
            <Link to="/legal-review-process" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] transition-fast" data-testid="nav-legal-review">Legal Review</Link>
            <Link to="/blog" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] transition-fast" data-testid="nav-blog">Blog</Link>
            <Link to="/billing" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] transition-fast" data-testid="nav-pricing">Pricing</Link>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {isAdmin && (
                  <Link to="/admin/login" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] text-sm flex items-center gap-1" data-testid="nav-admin">
                    <Settings className="w-4 h-4" /> Admin
                  </Link>
                )}
                <Link to="/dashboard" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] text-sm flex items-center gap-1" data-testid="nav-dashboard">
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Link>
                <Link to="/settings" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] text-sm flex items-center gap-1" data-testid="nav-settings">
                  <Settings className="w-4 h-4" /> Settings
                </Link>
                {user.tier === "free" && (
                  <Link to="/billing" data-testid="nav-upgrade">
                    <Button size="sm" className="bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)] font-semibold">
                      <Sparkles className="w-3.5 h-3.5 mr-1" /> Upgrade
                    </Button>
                  </Link>
                )}
                <ThemeToggle />
                <button
                  onClick={() => { logout(); navigate("/"); }}
                  className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] text-sm flex items-center gap-1"
                  data-testid="nav-logout"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" size="sm" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]">Log in</Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button size="sm" className="bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)] font-semibold">Start Free</Button>
                </Link>
                <ThemeToggle />
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 relative z-10">{children}</main>

      <footer className="border-t border-[var(--rg-border)] bg-[var(--rg-bg)] mt-16">
        <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-5 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <RegulensMark size={20} theme="dark" />
              <span className="font-heading font-bold text-[var(--rg-text-strong)]">REGULENS</span>
            </div>
            <p className="text-xs text-[var(--rg-muted)] leading-relaxed">
              Verified U.S. Shopify Readiness Intelligence. Coverage: 50 tax jurisdictions + 15 privacy-scored states.
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-muted)] mb-3">Trust</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]" data-testid="footer-about">About</Link></li>
              <li><Link to="/methodology" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]">Methodology</Link></li>
              <li><Link to="/legal-review-process" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]">Legal Review Process</Link></li>
              <li><Link to="/data-verification" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]">Data Verification</Link></li>
              <li><Link to="/transparency" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]" data-testid="footer-transparency">Data Transparency</Link></li>
              <li><Link to="/sample-report" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]">Sample Report</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-muted)] mb-3">Product</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/demo" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]">Demo Sandbox</Link></li>
              <li><Link to="/register" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]">Get Started</Link></li>
              <li><Link to="/billing" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]">Pricing</Link></li>
              <li><Link to="/blog" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]" data-testid="footer-blog">Blog</Link></li>
              <li><Link to="/testimonials" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]" data-testid="footer-testimonials">Customer Stories</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-muted)] mb-3">Support</div>
            <ul className="space-y-2 text-sm mb-3">
              <li><Link to="/contact" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/faq" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]" data-testid="footer-faq">FAQ</Link></li>
            </ul>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-muted)] mb-3 mt-4">Legal</div>
            <ul className="space-y-2 text-sm mb-3">
              <li><Link to="/privacy-policy" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms-of-service" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]" data-testid="footer-terms">Terms of Service</Link></li>
              <li><Link to="/refund-policy" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]" data-testid="footer-refund">Refund Policy</Link></li>
              <li><Link to="/federal-scope-notice" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]" data-testid="footer-scope">Coverage Scope &amp; Limitations</Link></li>
            </ul>
            <p className="text-xs text-[var(--rg-muted)] leading-relaxed">
              Regulens provides general informational content only and does not constitute legal advice, tax advice, or any professional advisory service. Coverage: 50 tax jurisdictions + 15 privacy-scored states.
            </p>
          </div>
        </div>
        <div className="border-t border-[var(--rg-border)] py-4 text-center text-[10px] text-[var(--rg-muted)] font-mono">
          © 2026 Regulens · Data verified against official government sources · {new Date().getFullYear()}
        </div>
      </footer>
      <AnalyticsNotice />
    </div>
  );
};

export default Layout;
